import { useCallback, useEffect, useRef, useState } from 'react'
import { snapshot } from 'valtio'

import store from '@/store'
import getAllProducts from '@/services/ProductService/getAllProducts'
import saveDesignTemplate from '@/services/DesignTemplateService/saveDesignTemplate'
import notifyError from '@/utils/notifyError'
import { ProductData } from '@/types/product'
import { DesignTemplateDetail } from '@/types/designTemplate'
import { normalizeTemplateData, validateTemplateData } from './utils/normalizeTemplateData'

interface DesignTemplateEditorModalProps {
  template: DesignTemplateDetail
  /** The product the template is designed on. */
  sku: string
  /** The section to draw on, so authoring starts in the right place. */
  sectionId?: string
  isOpen: boolean
  onClose: () => void
  onSaved?: () => void
}

/**
 * Hosts the customizer as a full-screen design surface for one template.
 *
 * The split of responsibility mirrors theme editing: the customizer draws and
 * hands back the design, and this side performs the write. That keeps the
 * customizer free of admin credentials, and keeps one save path for templates.
 */
const getBaseUrl = (): string => {
  const searchParams = new URLSearchParams(window.location.search)
  const urlParam =
    searchParams.get('customizer_url') || searchParams.get('customizerUrl') || searchParams.get('neatolabs')
  if (urlParam) return urlParam

  const match = window.location.hash.match(/#neatolabs=(.+)/)
  if (match) return match[1]

  const { companySetting } = snapshot(store)
  return companySetting?.profile_version === 'v2'
    ? import.meta.env.VITE_3D_PLUGIN_V2
    : import.meta.env.VITE_3D_PLUGIN_V1 || import.meta.env.VITE_3D_PLUGIN_V2
}

const buildCustomizerUrl = (baseUrl: string, params: Record<string, string | undefined>) => {
  const url = new URL(baseUrl)
  Object.entries(params).forEach(([key, value]) => {
    // Skip undefined rather than writing the string "undefined" into the query.
    if (value !== undefined) url.searchParams.set(key, value)
  })
  return url.toString()
}

const getOrigin = (url: string | null) => {
  if (!url) return '*'
  try {
    return new URL(url).origin
  } catch {
    return '*'
  }
}

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key)

const loadAllProductData = async (): Promise<ProductData[]> => {
  const { products } = snapshot(store)
  if (products.length) return JSON.parse(JSON.stringify(products))
  return (await getAllProducts()) || []
}

const DesignTemplateEditorModal = ({
  template,
  sku,
  sectionId,
  isOpen,
  onClose,
  onSaved,
}: DesignTemplateEditorModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || event.source !== iframeWindow) return

      const iframeOrigin = getOrigin(iframeUrl)
      if (iframeOrigin !== '*' && event.origin !== iframeOrigin) return

      const data = event.data
      if (!data) return
      const message = typeof data === 'object' ? (data as Record<string, any>) : null

      // The customizer asks for the catalogue when it needs to render a product.
      if (message?.action === 'getAllProduct') {
        try {
          const products = await loadAllProductData()
          iframeWindow.postMessage(
            {
              action: 'getAllProduct',
              type: 'getAllProduct',
              requestId: message.requestId || message.id,
              products,
              data: products,
            },
            iframeOrigin
          )
        } catch (error: any) {
          iframeWindow.postMessage(
            {
              action: 'getAllProduct',
              type: 'getAllProduct',
              requestId: message.requestId || message.id,
              products: [],
              data: [],
              error: error?.message || 'Failed to load products',
            },
            iframeOrigin
          )
        }
        return
      }

      // The customizer asks which template it is editing.
      if (message?.action === 'design-template-data' || message?.['design-template-data'] === true) {
        iframeWindow.postMessage(
          {
            'design-template-data': {
              id: template.id,
              name: template.name,
              sku,
              data: template.data ?? null,
            },
          },
          iframeOrigin
        )
        return
      }

      if (data === 'close-modal' || message?.type === 'close-modal' || message?.['close-modal']) {
        onClose()
        return
      }

      if (
        data === 'save-design-template' ||
        message?.type === 'save-design-template' ||
        (message && hasOwn(message, 'save-design-template'))
      ) {
        const rawDesign =
          message && hasOwn(message, 'save-design-template')
            ? message['save-design-template']
            : message?.design || message?.data?.design

        const fail = (reason: string) => {
          notifyError(reason)
          iframeWindow.postMessage({ 'design-template-saved': false, error: reason }, iframeOrigin)
        }

        if (!rawDesign) {
          fail('Template save failed: no design received')
          return
        }

        // Strip the authoring section's identity before storing. A template is
        // applied away from wherever it was drawn, so a section name or
        // material carried over would be stale on first use.
        const design = normalizeTemplateData(rawDesign)
        const problem = validateTemplateData(design)
        if (problem) {
          fail(problem)
          return
        }

        const thumbnail = message?.thumbnail || message?.thumbnail_url || template.thumbnail_url || ''

        try {
          const saveResult = await saveDesignTemplate(
            {
              name: template.name,
              description: template.description || '',
              thumbnail_url: thumbnail,
              is_active: template.is_active,
              is_generic: template.is_generic,
              priority: template.priority ?? 0,
              data: design,
              // A generic template is offered everywhere, so the API refuses it
              // alongside pinned sections. Section links are owned by the
              // product profile section form, so they are echoed untouched.
              section_ids: template.is_generic ? [] : template.sections?.map((section) => section.id) || [],
            },
            template.id
          )

          if (!saveResult) {
            fail('Template save failed')
            return
          }
        } catch (error: any) {
          const apiError = error?.response?.data?.error
          fail(apiError ? String(apiError).replace(/_/g, ' ') : error?.message || 'Template save failed')
          return
        }

        iframeWindow.postMessage({ 'design-template-saved': true }, iframeOrigin)
        onSaved?.()
      }
    },
    [template, sku, sectionId, iframeUrl, onClose, onSaved]
  )

  useEffect(() => {
    if (!isOpen) return
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isOpen, handleMessage])

  useEffect(() => {
    if (!isOpen) {
      setIframeUrl(null)
      return
    }

    const { currentUser } = snapshot(store)
    const storeName = currentUser?.selectedCompany?.store_name || ''

    setIframeUrl(
      buildCustomizerUrl(getBaseUrl(), {
        sku,
        store_name: storeName,
        development: 'true',
        // The mode switch the customizer reads to open its template designer.
        templateEditor: 'true',
        designTemplate: template.id,
        template_section_id: sectionId,
        orderPage: 'false',
        disable_legal_compliance: 'true',
        disable_profanity_check: 'true',
        disable_create_new_product: 'true',
      })
    )
  }, [isOpen, template.id, sku, sectionId])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] bg-white">
      {iframeUrl ? (
        <iframe
          ref={iframeRef}
          id="design-template-editor-iframe"
          src={iframeUrl}
          className="w-full h-full border-0 bg-white"
          allow="clipboard-read; clipboard-write"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full bg-white text-gray-500">Loading...</div>
      )}
    </div>
  )
}

export default DesignTemplateEditorModal
